/**
 * The carousel's own state machine — plan/tray-categories.md, "The design": a fixed arc of
 * five positions, 30 degrees apart, on a radius of 0.22 m, with the item nearest the viewer
 * always the active one. Deliberately Babylon-free, the same separation `WhiteboardLayout.ts`
 * keeps from `SceneRoot.ts`: this is the pure "which index is active, and where does each item
 * sit right now" logic, fully testable with plain numbers; `PaletteUI.ts` owns the meshes and
 * reads `slots()` to place them.
 *
 * Two numbers, not one, are what make every input (a controller's stick, a hand's drag, the
 * desktop's scroll or click) drive the same wheel:
 *
 * - `activeIndex` is the **committed** selection — what a category or an ink colour actually
 *   *is*, right now. It changes the instant an input says so (`stepBy`, `goTo`, or a drag
 *   crossing a boundary), because the wheel "applies the change as it spins" — there is no
 *   commit step, so nothing may wait for one.
 * - `visualPosition` is a **continuous**, purely cosmetic position that eases toward
 *   `activeIndex` over roughly 120 ms (`tick`) whenever nothing is actively dragging it, and is
 *   set directly, 1:1, while something is (`setVisualPosition`). `slots()` reads only this one,
 *   never `activeIndex` — so a discrete flick visibly spins into place instead of snapping, and
 *   a drag reads as touching an object rather than nudging a rate control.
 *
 * Wrapping is shortest-path throughout (`wrapDelta`): stepping past the last item lands on the
 * first, and the visual ease always turns whichever way is closer rather than sweeping the long
 * way round.
 */

export interface TrayWheelItem {
  readonly key: string;
  /** Display label, or '' for a plain colour swatch (an ink colour carries no label). */
  readonly label: string;
  readonly color: string;
}

export interface WheelSlot {
  readonly item: TrayWheelItem;
  readonly index: number;
  /** Signed, continuous — 0 at the front, positive/negative toward either flank. */
  readonly stepsFromFront: number;
  readonly angleRadians: number;
  /** Room-local, relative to the wheel's own front position: lateral offset. */
  readonly x: number;
  /** Room-local, relative to the wheel's own front position: depth behind it (always >= 0). */
  readonly z: number;
}

/** plan/tray-categories.md, "The shape: a fixed arc of positions." */
export const WHEEL_RADIUS = 0.22;
const WHEEL_STEP_DEGREES = 30;
const WHEEL_STEP_RADIANS = (WHEEL_STEP_DEGREES * Math.PI) / 180;

/** Nothing is drawn past +-60 degrees — two steps either side of the front. */
export const WHEEL_VISIBLE_STEPS = 2;

/**
 * Lateral hand travel worth exactly one item, R * sin(30 degrees) — "0.11 m of lateral travel
 * ... is one item" (plan/tray-categories.md). Exported so PaletteUI's hand-drag handler
 * converts metres to steps against the identical number this geometry already implies, rather
 * than a second constant that could drift from it.
 */
export const WHEEL_ITEM_PITCH_METERS = WHEEL_RADIUS * Math.sin(WHEEL_STEP_RADIANS);

/**
 * Time constant for the visual ease, chosen so it reads as ~95% settled within the ~120 ms the
 * design calls for (three time constants) — a carousel that teleports is a list
 * (tray-categories.md). Not confirmed on hardware, like every other feel number this project
 * ships with that caveat attached.
 */
const WHEEL_EASE_TAU_MS = 40;

/** Once the eased position is this close to the target, in step units, it snaps exactly onto
 *  it — so a settled wheel reports an exact integer position rather than an invisible residual
 *  that never quite reaches zero (an exponential ease never mathematically arrives). */
const WHEEL_SETTLE_EPSILON = 0.001;

function wrapIndex(index: number, count: number): number {
  return ((index % count) + count) % count;
}

/** Shortest signed distance from 0 to `delta` on a circle of circumference `count` — the same
 *  discipline `PaletteUI`'s row navigation already needs none of, because a row is not a
 *  circle, but a wheel with wrapping is exactly that. */
function wrapDelta(delta: number, count: number): number {
  if (count <= 0) {
    return delta;
  }

  let wrapped = delta % count;

  if (wrapped > count / 2) {
    wrapped -= count;
  } else if (wrapped < -count / 2) {
    wrapped += count;
  }

  return wrapped;
}

export class TrayWheel {
  private itemsList: TrayWheelItem[] = [];
  private activeIndexValue = 0;
  private visualPositionValue = 0;

  /**
   * Replaces the item list — a fresh catalogue, or a fresh set of ink colours. Resets
   * immediately to the first item, or to `preserveKey`'s item if it is still present (so a
   * `setCatalog` that happens to keep the same keys does not visibly reset what was showing).
   * No ease: a wholesale content change is not a step, so there is nothing to spin from.
   */
  setItems(items: readonly TrayWheelItem[], preserveKey?: string): void {
    this.itemsList = [...items];

    const preserved = preserveKey === undefined ? -1 : this.itemsList.findIndex((item) => item.key === preserveKey);

    this.activeIndexValue = preserved >= 0 ? preserved : 0;
    this.visualPositionValue = this.activeIndexValue;
  }

  get items(): readonly TrayWheelItem[] {
    return this.itemsList;
  }

  get activeIndex(): number {
    return this.activeIndexValue;
  }

  get activeItem(): TrayWheelItem | null {
    return this.itemsList[this.activeIndexValue] ?? null;
  }

  get visualPosition(): number {
    return this.visualPositionValue;
  }

  /**
   * Advances the active item by `delta` (typically +-1), wrapping — a controller's stick flick,
   * or the desktop's scroll notch. Applies live: the caller should react to `activeItem`
   * immediately after calling this, not wait for the visual ease `tick()` drives separately.
   * Returns whether anything actually changed (false for an empty wheel).
   */
  stepBy(delta: number): boolean {
    if (this.itemsList.length === 0) {
      return false;
    }

    const next = wrapIndex(this.activeIndexValue + delta, this.itemsList.length);

    if (next === this.activeIndexValue) {
      return false;
    }

    this.activeIndexValue = next;
    return true;
  }

  /** Rotates directly to the item named `key`, if it exists — a desktop click on a flanking
   *  item. A click on the item already active is a no-op, matching "clicking the front item
   *  does nothing, because it is already active." */
  goTo(key: string): boolean {
    const index = this.itemsList.findIndex((item) => item.key === key);

    if (index < 0 || index === this.activeIndexValue) {
      return false;
    }

    this.activeIndexValue = index;
    return true;
  }

  /**
   * Sets the continuous visual position directly, 1:1 — a hand's pinch-drag, tracked every
   * tick while the pinch holds. Immediately re-derives `activeIndex` from the rounded result
   * and applies it live if it changed, which is what makes "flicking past three pens changes
   * your pen three times" true for a drag exactly as it is for a stick. Returns whether the
   * active item changed.
   */
  setVisualPosition(position: number): boolean {
    this.visualPositionValue = position;
    return this.syncActiveFromVisual();
  }

  private syncActiveFromVisual(): boolean {
    if (this.itemsList.length === 0) {
      return false;
    }

    const rounded = wrapIndex(Math.round(this.visualPositionValue), this.itemsList.length);

    if (rounded === this.activeIndexValue) {
      return false;
    }

    this.activeIndexValue = rounded;
    return true;
  }

  /**
   * Eases the visual position toward `activeIndex` — call every tick the wheel is *not* being
   * actively dragged (a discrete step's own settle, and a drag's release settling onto the
   * nearest position, are both just this running once the direct 1:1 writes stop). A no-op,
   * harmlessly, if called while dragging too — `setVisualPosition` overwrites whatever this
   * would have computed on the very next call regardless — but callers should still gate this
   * behind "not currently dragging" so the two do not fight within the same tick.
   */
  tick(dtMs: number): void {
    const count = this.itemsList.length;

    if (count === 0) {
      return;
    }

    const wrapped = wrapDelta(this.activeIndexValue - this.visualPositionValue, count);
    const target = this.visualPositionValue + wrapped;
    const alpha = 1 - Math.exp(-dtMs / WHEEL_EASE_TAU_MS);

    this.visualPositionValue += (target - this.visualPositionValue) * alpha;

    if (Math.abs(wrapDelta(this.activeIndexValue - this.visualPositionValue, count)) < WHEEL_SETTLE_EPSILON) {
      this.visualPositionValue = this.activeIndexValue;
    }
  }

  /**
   * Every item currently within the visible arc, in no particular order — the renderer places
   * each independently and disables everything else. Reads only `visualPosition`, never
   * `activeIndex` directly, which is what makes an in-progress ease (or drag) show items mid-
   * transit rather than jumping the instant `activeIndex` itself changes.
   */
  slots(): WheelSlot[] {
    const count = this.itemsList.length;

    if (count === 0) {
      return [];
    }

    const result: WheelSlot[] = [];

    for (let index = 0; index < count; index += 1) {
      const stepsFromFront = wrapDelta(index - this.visualPositionValue, count);

      if (Math.abs(stepsFromFront) > WHEEL_VISIBLE_STEPS + WHEEL_SETTLE_EPSILON) {
        continue;
      }

      const angleRadians = stepsFromFront * WHEEL_STEP_RADIANS;

      result.push({
        item: this.itemsList[index]!,
        index,
        stepsFromFront,
        angleRadians,
        x: WHEEL_RADIUS * Math.sin(angleRadians),
        z: WHEEL_RADIUS * (1 - Math.cos(angleRadians)),
      });
    }

    return result;
  }
}
